import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import natsort from "natsort";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { LuArrowUpDown } from "react-icons/lu";

import { pathToMapDetail } from "../../../helpers/paths";
import Layout from "../../templates/Layout";
import ListSearch from "../../Form/ListSearch";
import useSort from "../../../hooks/useSort";
import { useListMapQuery } from "hooks/worker/useMap";
import { useListRobotQuery } from "hooks/worker/useRobot";
import { CleaningMapItem } from "types/response/map";
import { ScheduleItem } from "types/response/schedule";

const weekTexts = ["日", "月", "火", "水", "木", "金", "土"];

interface ScheduleMap extends CleaningMapItem {
  schedules: ScheduleItem[];
}

const MapSchedule = () => {
  const { data: maps = [] } = useListMapQuery();
  const { data: robots = [] } = useListRobotQuery();

  const [scheduleMaps, setScheduleMaps] = useState<ScheduleMap[]>([]);
  // テーブルの行
  const [searchedMaps, setSearchedMaps] = useState<ScheduleMap[]>([]);
  // 検索バーに入力された文字
  const [searched, setSearched] = useState("");
  const {
    order,
    orderBy,
    createSortHandler,
    stableSort,
    getComparator,
    orderByGetters,
  } = useSort<ScheduleMap>();

  useEffect(() => {
    const schedules = robots.reduce<{
      [mapId: string]: ScheduleItem[];
    }>((acc, robot) => {
      robot.schedules.forEach((s) => {
        if (!acc[s.mapId]) {
          acc[s.mapId] = [];
        }
        acc[s.mapId] = [...acc[s.mapId], s].sort(
          (a, b) => a.dayOfWeek - b.dayOfWeek
        );
      });
      return acc;
    }, {});
    setScheduleMaps(
      maps.map((m) => ({ ...m, schedules: schedules[m.mapId] || [] }))
    );
  }, [robots, maps]);

  // リストが見つからなかったときのメッセージを生成
  const getMessageBySearchedList = (
    searchedList: ScheduleMap[],
    searchString: string
  ): string => {
    if (searchedList.length === 0 && searchString.length !== 0) {
      return "検索結果はありません";
    } else if (searchedList.length === 0 && searchString.length === 0) {
      return "マップはありません";
    } else {
      return "";
    }
  };

  useEffect(() => {
    setSearchedMaps(
      scheduleMaps.sort((a, b) => natsort()(a.mapName, b.mapName))
    );
  }, [scheduleMaps]);

  return (
    <Layout title={"マップ一覧"}>
      <Box sx={{ display: "flex", mb: 1 }}>
        <ListSearch
          initialRows={scheduleMaps}
          searched={searched}
          setRows={setSearchedMaps}
          setSearched={setSearched}
          columnGetters={[
            (m) => m.mapName,
            (m) =>
              m.schedules
                .filter((s) => s.mapId)
                .map((s) => weekTexts[s.dayOfWeek])
                .join("、"),
          ]}
        />
      </Box>
      {searchedMaps.length > 0 ? (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "name"}
                      direction={orderBy === "name" ? order : "asc"}
                      hideSortIcon={true}
                      onClick={createSortHandler("name", [(m) => m.mapName])}
                    >
                      マップ
                      {orderBy === "name" ? undefined : (
                        <LuArrowUpDown style={{ opacity: 0.3 }} />
                      )}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "scheduleTexts"}
                      direction={orderBy === "scheduleTexts" ? order : "asc"}
                      hideSortIcon={true}
                      onClick={createSortHandler("scheduleTexts", [
                        (m) =>
                          // 日→月→火...の順にソートするためs.dayOfWeekでソート
                          m.schedules
                            .filter((s) => s.mapId)
                            .map((s) => s.dayOfWeek),
                      ])}
                    >
                      清掃予定
                      {orderBy === "scheduleTexts" ? undefined : (
                        <LuArrowUpDown style={{ opacity: 0.3 }} />
                      )}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stableSort(
                  searchedMaps,
                  getComparator(order, orderByGetters)
                ).map((m) => {
                  const scheduleTexts = m.schedules
                    .filter((s) => s.mapId)
                    .map((s) => weekTexts[s.dayOfWeek])
                    .join("、");
                  return (
                    <TableRow key={m.mapId}>
                      <TableCell>
                        <Link to={pathToMapDetail(m.mapId)}>{m.mapName}</Link>
                      </TableCell>
                      <TableCell>{scheduleTexts}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      ) : (
        <Typography variant="h5" sx={{ margin: 4 }}>
          {getMessageBySearchedList(searchedMaps, searched)}
        </Typography>
      )}
    </Layout>
  );
};

export default MapSchedule;
